/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatCurrency } from "@/lib/api";
import {
  HeartHalf, ArrowRight, CheckCircle, WarningCircle, XCircle,
} from "@phosphor-icons/react";

// Finance Health Dashboard ("Risk Status View") for the Finance Overview tab.
// Computes 8 KPIs by composing existing endpoints; each is click-through to its tab.
export default function FinanceHealthDashboard({ onJumpToTab }) {
  const [s, setS] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [ovR, recR, brR, pcR, taxR, donR, apvR] = await Promise.all([
          api.get("/finance/cashflow").catch(() => ({ data: {} })),
          api.get("/finance/ar/summary").catch(() => ({ data: {} })),
          api.get("/finance/bank-recon").catch(() => ({ data: { items: [] } })),
          api.get("/finance/petty-cash/summary").catch(() => ({ data: {} })),
          api.get("/finance/tax-liabilities").catch(() => ({ data: { items: [] } })),
          api.get("/finance/donors").catch(() => ({ data: { items: [] } })),
          api.get("/finance/approvals?status=pending").catch(() => ({ data: { items: [] } })),
        ]);
        if (!alive) return;
        // Cashflow rollup: derive opening cash and average monthly outflow
        const cfRows = ovR.data?.rows || [];
        const sumVals = (obj) => Object.values(obj || {}).reduce((a, v) => a + Number(v || 0), 0);
        const totalOutflow = cfRows.reduce((a, r) => a + sumVals(r.outflows) + Number(r.tax_outflow || 0), 0);
        const totalInflow  = cfRows.reduce((a, r) => a + sumVals(r.inflows), 0);
        const ov = {
          opening_cash: ovR.data?.opening_cash ?? (totalInflow - totalOutflow),
          avg_monthly_outflow: cfRows.length > 0 ? totalOutflow / cfRows.length : 0,
        };
        const arSummary = recR.data || {};
        const aging = {
          overdue_total: Number(arSummary["31_60"] || 0) + Number(arSummary["60_plus"] || 0),
          overdue_count: Math.max(0, Number(arSummary.count || 0)),
        };
        const bank = brR.data.items || brR.data || [];
        const petty = pcR.data || {};
        const taxes = taxR.data.items || taxR.data || [];
        const donors = donR.data.items || donR.data || [];
        const approvals = apvR.data.items || apvR.data || [];

        // KPI computation
        const cashOnHand = (ov.opening_cash ?? ov.closing_cash ?? 0);
        const monthlyBurn = Math.abs(ov.avg_monthly_outflow || ov.monthly_outflow || 0);
        const runwayMonths = monthlyBurn > 0 ? cashOnHand / monthlyBurn : null;

        const overdueAR = aging.overdue_total || 0;
        const overdueARCount = aging.overdue_count || 0;

        const unreconciledBanks = bank.filter((b) => Math.abs((b.statement_balance || 0) - (b.adjusted_book_balance || b.book_balance || 0)) > 1).length;

        const pettyBalance = petty.balance || 0;
        const pettyLimit = petty.float_limit || 200;
        const pettyHealthy = pettyBalance > (petty.warn_threshold || 50);

        const today = new Date().toISOString().slice(0, 10);
        const overdueTax = taxes.filter((t) => (t.status || "") !== "paid" && (t.due_date || "") && (t.due_date || "") < today);

        const donorDueSoon = donors.filter((d) => {
          if (!d.next_due) return false;
          const days = (new Date(d.next_due) - new Date(today)) / 86400000;
          return days >= 0 && days <= 14 && (d.status || "") !== "submitted";
        });
        const donorOverdue = donors.filter((d) => d.next_due && d.next_due < today && (d.status || "") !== "submitted");

        const pendingApprovals = approvals.length || (approvals.total ?? 0);

        if (alive) setS({
          cashOnHand, monthlyBurn, runwayMonths,
          overdueAR, overdueARCount,
          unreconciledBanks, totalBanks: bank.length,
          pettyBalance, pettyLimit, pettyHealthy,
          overdueTaxCount: overdueTax.length,
          overdueTaxAmount: overdueTax.reduce((a, t) => a + Number(t.amount || 0), 0),
          donorDueSoonCount: donorDueSoon.length,
          donorOverdueCount: donorOverdue.length,
          pendingApprovals,
        });
      } catch {
        if (alive) setS({});
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!s) {
    return <div className="dc-card p-6 text-sm text-slate-500">Computing finance health…</div>;
  }

  // Status classification helpers
  const runwayTone = s.runwayMonths == null ? "slate"
                  : s.runwayMonths >= 6 ? "green"
                  : s.runwayMonths >= 3 ? "yellow" : "red";
  const arTone = s.overdueAR <= 0 ? "green" : s.overdueAR < 10000 ? "yellow" : "red";
  const bankTone = s.unreconciledBanks === 0 ? "green" : s.unreconciledBanks <= 1 ? "yellow" : "red";
  const pettyTone = s.pettyHealthy ? "green" : "yellow";
  const taxTone = s.overdueTaxCount === 0 ? "green" : "red";
  const donorTone = s.donorOverdueCount > 0 ? "red"
                  : s.donorDueSoonCount > 0 ? "yellow" : "green";
  const apvTone = s.pendingApprovals === 0 ? "green"
                : s.pendingApprovals <= 5 ? "yellow" : "red";

  const jump = (tab) => onJumpToTab ? onJumpToTab(tab) : navigate(`/finance?tab=${tab}`);

  return (
    <div className="dc-card overflow-hidden" data-testid="finance-health-dashboard">
      <div className="border-b border-slate-100 px-5 py-3 flex items-center gap-2">
        <HeartHalf size={18} className="text-[#1E5CBA]" weight="fill" />
        <h3 className="font-display text-[15px] font-semibold text-slate-900">Finance Health Dashboard</h3>
        <span className="ml-auto rounded-full bg-[#1E5CBA]/10 text-[#1E5CBA] px-2 py-0.5 text-[10px] font-mono-alt uppercase tracking-widest">
          Risk status view
        </span>
      </div>

      <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
        <HealthTile
          testid="health-runway" tone={runwayTone} onClick={() => jump("cashflow")}
          label="Cash runway"
          value={s.runwayMonths == null ? "—" : `${s.runwayMonths.toFixed(1)} mo`}
          subtitle={`Cash on hand ${formatCurrency(s.cashOnHand)} · burn ${formatCurrency(s.monthlyBurn)}/mo`}
        />
        <HealthTile
          testid="health-ar" tone={arTone} onClick={() => jump("ar")}
          label="Overdue receivables"
          value={formatCurrency(s.overdueAR)}
          subtitle={`${s.overdueARCount} invoice(s) past due`}
        />
        <HealthTile
          testid="health-bank" tone={bankTone} onClick={() => jump("bank_recon")}
          label="Unreconciled banks"
          value={`${s.unreconciledBanks}/${s.totalBanks}`}
          subtitle={s.unreconciledBanks === 0 ? "All banks in agreement" : "Run reconciliation"}
        />
        <HealthTile
          testid="health-petty" tone={pettyTone} onClick={() => jump("petty_cash")}
          label="Petty cash float"
          value={formatCurrency(s.pettyBalance)}
          subtitle={s.pettyHealthy ? `Healthy (limit ${formatCurrency(s.pettyLimit)})` : "Below warn threshold — replenish"}
        />
        <HealthTile
          testid="health-tax" tone={taxTone} onClick={() => jump("tax")}
          label="Overdue tax"
          value={s.overdueTaxCount === 0 ? "None" : `${s.overdueTaxCount} due`}
          subtitle={s.overdueTaxAmount ? formatCurrency(s.overdueTaxAmount) : "All payments up to date"}
        />
        <HealthTile
          testid="health-donor" tone={donorTone} onClick={() => jump("donor")}
          label="Donor reports"
          value={s.donorOverdueCount > 0
                  ? `${s.donorOverdueCount} overdue`
                  : s.donorDueSoonCount > 0
                    ? `${s.donorDueSoonCount} due ≤14d`
                    : "On track"}
          subtitle={s.donorOverdueCount > 0
                    ? "File outstanding reports immediately"
                    : s.donorDueSoonCount > 0 ? "Prepare submissions" : "All caught up"}
        />
        <HealthTile
          testid="health-approvals" tone={apvTone} onClick={() => navigate("/approvals")}
          label="Pending approvals"
          value={s.pendingApprovals}
          subtitle={s.pendingApprovals === 0 ? "Inbox is clear" : "Awaiting your action"}
        />
        <HealthTile
          testid="health-payroll" tone={apvTone === "red" ? "yellow" : apvTone}
          onClick={() => jump("payroll")}
          label="Payroll review queue"
          value={s.pendingApprovals}
          subtitle="Combined finance + admin queue"
        />
      </div>
    </div>
  );
}

function HealthTile({ testid, tone, onClick, label, value, subtitle }) {
  const palette = {
    green:  { border: "border-l-emerald-500", bg: "bg-emerald-50/40", text: "text-emerald-700", icon: CheckCircle, dot: "bg-emerald-500" },
    yellow: { border: "border-l-amber-500", bg: "bg-amber-50/40", text: "text-amber-700", icon: WarningCircle, dot: "bg-amber-500" },
    red:    { border: "border-l-red-500", bg: "bg-red-50/40", text: "text-red-700", icon: XCircle, dot: "bg-red-500" },
    slate:  { border: "border-l-slate-300", bg: "bg-slate-50/30", text: "text-slate-500", icon: CheckCircle, dot: "bg-slate-300" },
  }[tone];
  const Icon = palette.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`rounded-lg border border-slate-200 ${palette.border} ${palette.bg} p-3 text-left transition hover:shadow-md hover:border-slate-300`}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className={`inline-block h-2 w-2 rounded-full ${palette.dot} animate-pulse`}></span>
        <span className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">{label}</span>
        <Icon size={14} weight="fill" className={`ml-auto ${palette.text} opacity-70`} />
      </div>
      <div className="font-display text-xl font-semibold text-slate-900 tabular-nums truncate">{value}</div>
      <div className="text-[11px] text-slate-500 mt-1 line-clamp-2">{subtitle}</div>
      <div className="mt-2 flex items-center gap-1 text-[10px] text-slate-400">
        Tap to open <ArrowRight size={10} />
      </div>
    </button>
  );
}
